import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { AgentEvaluatorAssignmentDialog } from "../../components/agents/AgentEvaluatorAssignmentDialog";
import { CreateAgentEvaluatorAssignmentDialog } from "../../components/agents/CreateAgentEvaluatorAssignmentDialog";
import { Badge, Icon, SettingsList, SettingsListItem, Text, View } from "../../components/base";
import { EmptyMessage } from "../../components/EmptyMessage";
import { useAppDispatch, useAppSelector } from "../../redux";
import { agentEvaluatorAssignmentActions, AgentEvaluatorAssignment } from "../../redux/reducers/agentEvaluatorAssignmentSlice";
import { evaluatorActions } from "../../redux/reducers/evaluatorSlice";
import { getSamplingRatePercentage } from "../../utils/samplingRate";

export const AgentEvaluationAssignmentsTab = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const dispatch = useAppDispatch();
  const assignmentState = useAppSelector((state) => state.agentEvaluatorAssignment);
  const evaluatorEntities = useAppSelector((state) => state.evaluator.entities);

  const assignments = useMemo(() => {
    return assignmentState.ids.map((id) => assignmentState.entities[id]).filter((assignment): assignment is AgentEvaluatorAssignment => Boolean(assignment));
  }, [assignmentState.entities, assignmentState.ids]);

  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    if (!agentId) {
      return;
    }

    dispatch(agentEvaluatorAssignmentActions.getAssignments({ agentId }));
  }, [agentId, dispatch]);

  useEffect(() => {
    dispatch(evaluatorActions.get({}));
  }, [dispatch]);

  const selectedAssignment = useMemo(() => {
    if (!selectedAssignmentId) {
      return null;
    }
    return assignmentState.entities[selectedAssignmentId] || null;
  }, [assignmentState.entities, selectedAssignmentId]);

  const selectedEvaluator = selectedAssignment ? evaluatorEntities[selectedAssignment.evaluatorId] : null;

  const openAssignmentDialog = (assignment: AgentEvaluatorAssignment) => {
    setSelectedAssignmentId(assignment.id);
    setIsAssignmentDialogOpen(true);
  };

  const closeAssignmentDialog = () => {
    setIsAssignmentDialogOpen(false);
    setSelectedAssignmentId(null);
  };

  const isLoadingAssignments = assignmentState.isLoading;
  const noAssignments = !isLoadingAssignments && assignments.length === 0;

  return (
    <View className="flex flex-col gap-4 p-4">
      <View className="flex flex-col gap-1">
        <Text className="text-lg font-semibold">Evaluator Assignments</Text>
        <Text className="text-sm text-gray-500 dark:text-gray-400">Connect evaluators to automatically evaluate this agent's sessions.</Text>
      </View>

      {isLoadingAssignments ? (
        <View className="flex items-center justify-center py-8">
          <Text className="text-sm text-gray-500 dark:text-gray-400">Loading assignments...</Text>
        </View>
      ) : noAssignments ? (
        <EmptyMessage
          title="No evaluators assigned"
          description="Assign an evaluator to automatically evaluate agent sessions."
          iconName="checklist"
          buttonText={null}
        />
      ) : (
        <SettingsList>
          {assignments.map((assignment) => {
            const evaluator = evaluatorEntities[assignment.evaluatorId];
            const rawSampling = assignment.samplingRate;
            const badgeVariant = rawSampling === null || rawSampling === undefined ? "success" : rawSampling === 0 ? "error" : "brand";
            const icon = (evaluator?.title?.trim()?.[0] || "E").toUpperCase();

            return (
              <div
                key={assignment.id}
                onClick={() => openAssignmentDialog(assignment)}
                className="cursor-pointer"
              >
                <SettingsListItem
                  icon={icon}
                  title={evaluator?.title || "Evaluator"}
                  subtitle={
                    <View className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                      <Badge variant={badgeVariant}>{getSamplingRatePercentage(rawSampling ?? undefined)}</Badge>
                      <Text className="text-xs">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>{" "}
                        <span className="text-gray-900 dark:text-white">{evaluator?.evalType || "Unknown"}</span>
                      </Text>
                      <Text className="text-xs">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Target:</span>{" "}
                        <span className="text-gray-900 dark:text-white">{evaluator?.targetFormat || "Unknown"}</span>
                      </Text>
                    </View>
                  }
                  right={
                    <Icon
                      name="chevron-right"
                      size={16}
                      className="text-gray-400 flex-shrink-0"
                    />
                  }
                />
              </div>
            );
          })}
          <button
            onClick={() => setIsCreateDialogOpen(true)}
            className="flex flex-row items-center gap-2 px-4 py-3 w-full text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group"
          >
            <View className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 group-hover:border-brand dark:group-hover:border-brand-light transition-colors">
              <Icon
                name="add"
                size={14}
                className="text-gray-400 dark:text-gray-500 group-hover:text-brand dark:group-hover:text-brand-light transition-colors"
              />
            </View>
            <Text className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
              Add assignment
            </Text>
          </button>
        </SettingsList>
      )}

      <AgentEvaluatorAssignmentDialog
        agentId={agentId || ""}
        assignment={selectedAssignment}
        evaluator={selectedEvaluator}
        isOpen={isAssignmentDialogOpen && Boolean(selectedAssignment)}
        onClose={closeAssignmentDialog}
      />
      {agentId ? (
        <CreateAgentEvaluatorAssignmentDialog
          agentId={agentId}
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
        />
      ) : null}
    </View>
  );
};
